import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CrmPage from "./CrmPage";
import { crmFetch, crmShareUrl } from "./crmApi";
import "./Crm.css";

/**
 * Settings for the studio's public enquiry form. Share the link in an
 * Instagram bio or on the website and submissions arrive as leads, already
 * flagged for follow-up today.
 */
function CrmEnquiryForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planBlocked, setPlanBlocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [typesText, setTypesText] = useState("");
  const [shareOrigin, setShareOrigin] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await crmFetch("/api/crm/enquiry-form");
      setForm(d.form);
      setShareOrigin(d.share_origin || null);
      setTypesText((d.form.event_types || []).join(", "));
    } catch (e) {
      if (e.status === 401) navigate("/login");
      if (e.status === 403 && e.data?.code === "upgrade_required") setPlanBlocked(true);
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const link = form ? crmShareUrl(`/enquire/${form.share_token}`, shareOrigin) : "";

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const d = await crmFetch("/api/crm/enquiry-form", {
        method: "PUT",
        body: {
          enabled: form.enabled,
          headline: form.headline,
          intro: form.intro,
          thank_you: form.thank_you,
          event_types: typesText.split(",").map((s) => s.trim()).filter(Boolean),
        },
      });
      setForm(d.form);
      setShareOrigin(d.share_origin || null);
      setTypesText((d.form.event_types || []).join(", "));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy your enquiry form link:", link);
    }
  };

  return (
    <CrmPage
      title="🔗 Enquiry Form"
      subtitle="A public form that turns visitors into leads automatically"
      loading={loading || !form}
      planBlocked={planBlocked}
      actions={
        form && (
          <button className="crm-primary-btn" onClick={copy}>
            {copied ? "✓ Copied!" : "🔗 Copy link"}
          </button>
        )
      }
    >
      {form && (
        <>
          <div className="crm-card">
            <h3 className="crm-section-title">Your form link</h3>
            <div className="crm-link-box">
              <code>{link}</code>
              <div className="crm-quote-card-actions" style={{ marginTop: 0 }}>
                <button className="crm-btn ghost" onClick={copy}>
                  {copied ? "✓ Copied" : "Copy"}
                </button>
                <a className="crm-btn ghost" href={link} target="_blank" rel="noopener noreferrer"
                   style={{ textDecoration: "none" }}>
                  👁 Preview
                </a>
              </div>
            </div>
            <p className="crm-row-sub" style={{ marginTop: 10 }}>
              Put this in your Instagram bio, on your website, or in a WhatsApp auto-reply.
              Every submission creates a lead in <strong>New Enquiry</strong> with a follow-up
              set for today.
              {form.submission_count > 0 && (
                <> So far it has brought in <strong>{form.submission_count}</strong> enquir
                {form.submission_count === 1 ? "y" : "ies"}.</>
              )}
            </p>

            <label className="crm-checkline" style={{ marginTop: 14 }}>
              <input
                type="checkbox"
                checked={!!form.enabled}
                onChange={(e) => { setForm((f) => ({ ...f, enabled: e.target.checked })); setSaved(false); }}
              />
              Accept new submissions
            </label>
            {!form.enabled && (
              <p className="crm-row-sub" style={{ color: "#dc2626" }}>
                The form is switched off — visitors see a short "not accepting enquiries" note.
              </p>
            )}
          </div>

          <div className="crm-card" style={{ marginTop: 18 }}>
            <h3 className="crm-section-title">What visitors see</h3>
            {error && <div className="crm-form-error">{error}</div>}
            <div className="crm-form-grid">
              <div className="crm-field full">
                <label>Headline</label>
                <input value={form.headline || ""} onChange={set("headline")}
                       placeholder="Tell us about your event" />
              </div>
              <div className="crm-field full">
                <label>Intro text</label>
                <textarea value={form.intro || ""} onChange={set("intro")}
                          placeholder="Share a few details and we'll get back to you…" />
              </div>
              <div className="crm-field full">
                <label>Event types to offer (comma separated)</label>
                <input
                  value={typesText}
                  onChange={(e) => { setTypesText(e.target.value); setSaved(false); }}
                  placeholder="Wedding, Engagement, Birthday, Corporate"
                />
                <span className="crm-row-sub">
                  Leave empty to let visitors type their own event type.
                </span>
              </div>
              <div className="crm-field full">
                <label>Thank-you message</label>
                <textarea value={form.thank_you || ""} onChange={set("thank_you")}
                          placeholder="Thank you! We've received your enquiry…" />
              </div>
            </div>
            <div className="crm-modal-actions">
              {saved && <span className="crm-saved-note">✓ Saved</span>}
              <button className="crm-btn primary" disabled={saving} onClick={save}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </>
      )}
    </CrmPage>
  );
}

export default CrmEnquiryForm;
